/**
 * The workspace's shared primitives: buttons, fields, alerts, dialogs, toasts.
 *
 * These carry the design system's decisions so the pages do not restate them:
 * teal is the only filled action colour and there is one per view; status and
 * feedback always carry a word, never colour alone; focus rings are never
 * removed; every interactive target is at least 44px on touch layouts; and the
 * one polite live region for toasts lives here, so announcements cannot
 * multiply into a chorus.
 *
 * Everything is styled with tokens from `styles/tokens.css`, matching how the
 * landing page's components are built — inline styles over the token
 * vocabulary, with `styles/workspace.css` carrying only what needs media
 * queries.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type ReactNode,
} from 'react';

// --------------------------------------------------------------------------- //
// Buttons
// --------------------------------------------------------------------------- //

type ButtonVariant = 'primary' | 'secondary' | 'outline' | 'quiet' | 'danger';

const BUTTON_STYLES: Record<ButtonVariant, CSSProperties> = {
  primary: {
    background: 'var(--action-primary)',
    color: 'var(--text-on-primary)',
    border: '1px solid transparent',
  },
  secondary: {
    background: 'var(--action-secondary-surface)',
    color: 'var(--action-secondary-text)',
    border: '1px solid transparent',
  },
  outline: {
    background: 'transparent',
    color: 'var(--text-primary)',
    border: '1px solid var(--border-strong)',
  },
  quiet: {
    background: 'transparent',
    color: 'var(--text-secondary)',
    border: '1px solid transparent',
  },
  danger: {
    background: 'transparent',
    color: 'var(--status-error-fg)',
    border: '1px solid var(--status-error-fg)',
  },
};

export function Button({
  variant = 'secondary',
  size = 'md',
  type = 'button',
  disabled = false,
  busy = false,
  onClick,
  children,
  style,
  ...aria
}: {
  readonly variant?: ButtonVariant;
  readonly size?: 'sm' | 'md';
  readonly type?: 'button' | 'submit';
  readonly disabled?: boolean;
  /** Disables and names the in-flight state without swapping the label out. */
  readonly busy?: boolean;
  readonly onClick?: () => void;
  readonly children: ReactNode;
  readonly style?: CSSProperties;
  readonly 'aria-label'?: string;
  readonly 'aria-expanded'?: boolean;
  readonly 'aria-pressed'?: boolean;
}): ReactNode {
  return (
    <button
      type={type}
      disabled={disabled || busy}
      aria-busy={busy || undefined}
      onClick={onClick}
      {...aria}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 8,
        minHeight: 44,
        padding: size === 'sm' ? '8px 14px' : '10px 20px',
        borderRadius: 'var(--radius-button)',
        font: size === 'sm' ? 'var(--type-button-sm)' : 'var(--type-button-md)',
        cursor: disabled || busy ? 'default' : 'pointer',
        opacity: disabled && !busy ? 0.55 : 1,
        whiteSpace: 'nowrap',
        transition: 'var(--transition-control)',
        ...BUTTON_STYLES[variant],
        ...style,
      }}
    >
      {children}
    </button>
  );
}

// --------------------------------------------------------------------------- //
// Fields
// --------------------------------------------------------------------------- //

const FIELD_LABEL: CSSProperties = {
  display: 'block',
  font: 'var(--type-caption)',
  letterSpacing: 'var(--type-caption-tracking)',
  color: 'var(--text-secondary)',
  marginBottom: 6,
};

const FIELD_INPUT: CSSProperties = {
  width: '100%',
  minHeight: 'var(--field-height)',
  padding: '10px 14px',
  borderRadius: 'var(--radius-input)',
  border: '1px solid var(--border-input)',
  background: 'var(--surface-card)',
  color: 'var(--text-primary)',
  font: 'var(--type-body-md)',
};

export function TextField({
  label,
  value,
  onChange,
  hint,
  error,
  required = false,
  maxLength,
  dir,
  placeholder,
  type = 'text',
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly hint?: string;
  readonly error?: string | null;
  readonly required?: boolean;
  readonly maxLength?: number;
  /** Identifiers — URLs, emails, references — stay LTR under RTL. */
  readonly dir?: 'ltr' | 'rtl' | 'auto';
  readonly placeholder?: string;
  readonly type?: 'text' | 'email' | 'url';
}): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <label htmlFor={id} style={FIELD_LABEL}>
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        required={required}
        maxLength={maxLength}
        placeholder={placeholder}
        dir={dir}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
          undefined
        }
        style={{
          ...FIELD_INPUT,
          ...(error ? { borderColor: 'var(--status-error-fg)' } : {}),
        }}
      />
      {hint ? (
        <p
          id={hintId}
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            marginTop: 4,
          }}
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          style={{
            font: 'var(--type-caption)',
            color: 'var(--status-error-fg)',
            marginTop: 4,
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

export function TextAreaField({
  label,
  value,
  onChange,
  hint,
  error,
  rows = 4,
  maxLength,
  labelExtra,
}: {
  readonly label: string;
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly hint?: string;
  readonly error?: string | null;
  readonly rows?: number;
  readonly maxLength?: number;
  /** A control rendered beside the label — the Markdown edit/preview toggle. */
  readonly labelExtra?: ReactNode;
}): ReactNode {
  const id = useId();
  const hintId = `${id}-hint`;
  const errorId = `${id}-error`;

  return (
    <div style={{ marginBottom: 'var(--space-md)' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        <label htmlFor={id} style={FIELD_LABEL}>
          {label}
        </label>
        {labelExtra}
      </div>
      <textarea
        id={id}
        value={value}
        rows={rows}
        maxLength={maxLength}
        onChange={(event) => {
          onChange(event.target.value);
        }}
        aria-invalid={error ? true : undefined}
        aria-describedby={
          [hint ? hintId : null, error ? errorId : null].filter(Boolean).join(' ') ||
          undefined
        }
        style={{
          ...FIELD_INPUT,
          minHeight: rows * 24 + 24,
          resize: 'vertical',
          lineHeight: 1.6,
          ...(error ? { borderColor: 'var(--status-error-fg)' } : {}),
        }}
      />
      {hint ? (
        <p
          id={hintId}
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            marginTop: 4,
          }}
        >
          {hint}
        </p>
      ) : null}
      {error ? (
        <p
          id={errorId}
          style={{
            font: 'var(--type-caption)',
            color: 'var(--status-error-fg)',
            marginTop: 4,
          }}
        >
          {error}
        </p>
      ) : null}
    </div>
  );
}

/**
 * A radio group with optional per-choice definitions.
 *
 * A `fieldset` with real radio inputs rather than styled buttons, because the
 * approval dialog and the proposal form both need "pick one, and understand
 * what each one means" — which is exactly what the platform control already
 * announces correctly.
 */
export function ChoiceGroup({
  legend,
  value,
  onChange,
  choices,
  hint,
  error,
  inline = false,
}: {
  readonly legend: string;
  readonly value: string | null;
  readonly onChange: (value: string) => void;
  readonly choices: readonly {
    readonly value: string;
    readonly label: string;
    readonly definition?: string | undefined;
  }[];
  readonly hint?: string;
  readonly error?: string | null;
  readonly inline?: boolean;
}): ReactNode {
  const name = useId();

  return (
    <fieldset
      style={{
        border: 'none',
        padding: 0,
        margin: '0 0 var(--space-md)',
      }}
    >
      <legend style={FIELD_LABEL}>{legend}</legend>
      {hint ? (
        <p
          style={{
            font: 'var(--type-caption)',
            color: 'var(--text-muted)',
            margin: '0 0 8px',
          }}
        >
          {hint}
        </p>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexDirection: inline ? 'row' : 'column',
          flexWrap: 'wrap',
          gap: inline ? 10 : 8,
        }}
      >
        {choices.map((choice) => {
          const selected = value === choice.value;
          return (
            <label
              key={choice.value}
              style={{
                display: 'flex',
                gap: 10,
                alignItems: 'flex-start',
                padding: '10px 14px',
                minHeight: 44,
                borderRadius: 'var(--radius-input)',
                border: `1px solid ${selected ? 'var(--action-primary)' : 'var(--border-input)'}`,
                background: selected
                  ? 'var(--action-primary-subtle)'
                  : 'var(--surface-card)',
                cursor: 'pointer',
                flex: inline ? '0 0 auto' : undefined,
              }}
            >
              <input
                type="radio"
                name={name}
                value={choice.value}
                checked={selected}
                onChange={() => {
                  onChange(choice.value);
                }}
                style={{ marginTop: 3, accentColor: 'var(--mj-teal-600)' }}
              />
              <span>
                <span
                  style={{ font: 'var(--type-body-md)', color: 'var(--text-primary)' }}
                >
                  {choice.label}
                </span>
                {choice.definition ? (
                  <span
                    style={{
                      display: 'block',
                      font: 'var(--type-caption)',
                      color: 'var(--text-muted)',
                      marginTop: 2,
                    }}
                  >
                    {choice.definition}
                  </span>
                ) : null}
              </span>
            </label>
          );
        })}
      </div>
      {error ? (
        <p
          style={{
            font: 'var(--type-caption)',
            color: 'var(--status-error-fg)',
            marginTop: 4,
          }}
        >
          {error}
        </p>
      ) : null}
    </fieldset>
  );
}

// --------------------------------------------------------------------------- //
// Feedback
// --------------------------------------------------------------------------- //

type AlertTone = 'info' | 'success' | 'warning' | 'error' | 'neutral';

const ALERT_TONES: Record<AlertTone, { bg: string; fg: string }> = {
  info: { bg: 'var(--status-info-bg)', fg: 'var(--status-info-fg)' },
  success: { bg: 'var(--status-success-bg)', fg: 'var(--status-success-fg)' },
  warning: { bg: 'var(--status-warning-bg)', fg: 'var(--status-warning-fg)' },
  error: { bg: 'var(--status-error-bg)', fg: 'var(--status-error-fg)' },
  neutral: { bg: 'var(--status-neutral-bg)', fg: 'var(--status-neutral-fg)' },
};

export function InlineAlert({
  tone,
  title,
  children,
  role,
}: {
  readonly tone: AlertTone;
  readonly title?: string;
  readonly children?: ReactNode;
  /** `alert` for interruptive failures; default is a plain region. */
  readonly role?: 'alert' | 'status';
}): ReactNode {
  const colors = ALERT_TONES[tone];
  return (
    <div
      role={role}
      style={{
        background: colors.bg,
        color: colors.fg,
        borderRadius: 'var(--radius-alert)',
        padding: '14px 18px',
        marginBottom: 'var(--space-md)',
        font: 'var(--type-body-md)',
      }}
    >
      {title ? (
        <strong style={{ display: 'block', marginBottom: children ? 4 : 0 }}>
          {title}
        </strong>
      ) : null}
      {children}
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
  compact = false,
}: {
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
  readonly compact?: boolean;
}): ReactNode {
  return (
    <div
      style={{
        textAlign: 'center',
        padding: compact ? 'var(--space-md)' : 'var(--space-xl)',
        color: 'var(--text-muted)',
        font: 'var(--type-body-md)',
      }}
    >
      <p style={{ color: 'var(--text-secondary)', marginBottom: description ? 6 : 0 }}>
        {title}
      </p>
      {description ? (
        <p style={{ marginBottom: action ? 12 : 0 }}>{description}</p>
      ) : null}
      {action}
    </div>
  );
}

export function Tag({
  children,
  tone = 'neutral',
  dashed = false,
  title,
}: {
  readonly children: ReactNode;
  readonly tone?: AlertTone;
  /** The Backlog suggested-priority treatment: dashed, muted, unmistakable. */
  readonly dashed?: boolean;
  readonly title?: string;
}): ReactNode {
  const colors = ALERT_TONES[tone];
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '2px 10px',
        borderRadius: 'var(--radius-tag)',
        font: 'var(--type-caption)',
        letterSpacing: 'var(--type-caption-tracking)',
        ...(dashed
          ? {
              background: 'transparent',
              color: 'var(--text-muted)',
              border: '1px dashed var(--border-strong)',
            }
          : {
              background: colors.bg,
              color: colors.fg,
              border: '1px solid transparent',
            }),
      }}
    >
      {children}
    </span>
  );
}

export function Spinner({ label }: { readonly label: string }): ReactNode {
  return (
    <div
      role="status"
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: 'var(--space-lg)',
        color: 'var(--text-muted)',
        font: 'var(--type-body-md)',
        justifyContent: 'center',
      }}
    >
      <span aria-hidden="true" className="mj-spinner" />
      {label}
    </div>
  );
}

// --------------------------------------------------------------------------- //
// Dialogs
// --------------------------------------------------------------------------- //

/**
 * A confirmation dialog: title, one sentence, a "What happens" list, an
 * optional field group, cancel plus one primary action.
 *
 * Built on `<dialog>`, which supplies focus containment, `Escape`, and the
 * top layer for free. Below 768px `styles/workspace.css` turns it into a
 * bottom sheet — same element, same semantics, different geometry.
 */
export function ConfirmDialog({
  open,
  title,
  body,
  whatHappensLabel,
  effects,
  confirmLabel,
  cancelLabel,
  onConfirm,
  onCancel,
  busy = false,
  confirmDisabled = false,
  error,
  danger = false,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly body: string;
  readonly whatHappensLabel: string;
  readonly effects: readonly string[];
  readonly confirmLabel: string;
  readonly cancelLabel: string;
  readonly onConfirm: () => void;
  readonly onCancel: () => void;
  readonly busy?: boolean;
  /**
   * Refuse the confirmation without hiding it. For a dialog whose confirm
   * would be a no-op or is not yet answerable — nothing has changed, or the
   * current value has not loaded — so the person can see the action exists
   * and see that it is not available yet.
   */
  readonly confirmDisabled?: boolean;
  readonly error?: ReactNode;
  readonly danger?: boolean;
  /** The optional field group — reason, priority, GitHub links. */
  readonly children?: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  // Where focus goes back to when the dialog closes. `showModal` moves focus
  // into the dialog; nothing moves it back on close, and losing one's place
  // after cancelling a claim is exactly the kind of paper cut a keyboard
  // user hits twenty times a day.
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      restoreTo.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    }
    if (!open && dialog.open) {
      dialog.close();
      restoreTo.current?.focus();
      restoreTo.current = null;
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="mj-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
      onClick={(event) => {
        // A click on the backdrop — outside the padded content — cancels.
        if (event.target === ref.current && !busy) onCancel();
      }}
    >
      <form
        method="dialog"
        onSubmit={(event) => {
          event.preventDefault();
          if (!busy && !confirmDisabled) onConfirm();
        }}
        style={{ padding: 'var(--space-lg)' }}
      >
        <h2
          id={titleId}
          style={{
            font: 'var(--type-heading-md)',
            color: 'var(--text-primary)',
            marginBottom: 8,
          }}
        >
          {title}
        </h2>
        <p
          style={{
            font: 'var(--type-body-md)',
            color: 'var(--text-secondary)',
            marginBottom: 14,
          }}
        >
          {body}
        </p>

        <p style={{ ...FIELD_LABEL, marginBottom: 4 }}>{whatHappensLabel}</p>
        <ul
          style={{
            margin: '0 0 var(--space-md)',
            paddingInlineStart: 20,
            color: 'var(--text-secondary)',
            font: 'var(--type-body-md)',
            lineHeight: 1.7,
          }}
        >
          {effects.map((effect) => (
            <li key={effect}>{effect}</li>
          ))}
        </ul>

        {children}
        {error}

        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 10,
            marginTop: 'var(--space-md)',
            flexWrap: 'wrap',
          }}
        >
          <Button variant="quiet" onClick={onCancel} disabled={busy}>
            {cancelLabel}
          </Button>
          <Button
            variant={danger ? 'danger' : 'primary'}
            type="submit"
            busy={busy}
            disabled={confirmDisabled}
          >
            {confirmLabel}
          </Button>
        </div>
      </form>
    </dialog>
  );
}

/**
 * A focused side panel: the same native `<dialog>` mechanics as
 * {@link ConfirmDialog} — focus containment, `Escape`, top layer, focus
 * restored to the opener on close — with different geometry. On desktop it
 * docks to the inline-end edge at full height; below 768px
 * `styles/workspace.css` turns it into a full-width bottom sheet. Logical
 * properties throughout, so RTL docks it to the correct edge for free.
 */
export function Drawer({
  open,
  title,
  closeLabel,
  onClose,
  children,
}: {
  readonly open: boolean;
  readonly title: string;
  readonly closeLabel: string;
  readonly onClose: () => void;
  readonly children: ReactNode;
}): ReactNode {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const restoreTo = useRef<HTMLElement | null>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (dialog === null) return;
    if (open && !dialog.open) {
      restoreTo.current =
        document.activeElement instanceof HTMLElement ? document.activeElement : null;
      dialog.showModal();
    }
    if (!open && dialog.open) {
      dialog.close();
      restoreTo.current?.focus();
      restoreTo.current = null;
    }
  }, [open]);

  return (
    <dialog
      ref={ref}
      className="mj-drawer"
      aria-labelledby={titleId}
      onCancel={(event) => {
        event.preventDefault();
        onClose();
      }}
      onClick={(event) => {
        if (event.target === ref.current) onClose();
      }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          height: '100%',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 10,
            padding: 'var(--space-md) var(--space-lg)',
            borderBlockEnd: '1px solid var(--border-hairline)',
          }}
        >
          <h2
            id={titleId}
            style={{ font: 'var(--type-heading-md)', color: 'var(--text-primary)' }}
          >
            {title}
          </h2>
          <Button size="sm" variant="quiet" onClick={onClose}>
            {closeLabel}
          </Button>
        </div>
        <div style={{ overflowY: 'auto', padding: 'var(--space-lg)', minHeight: 0 }}>
          {children}
        </div>
      </div>
    </dialog>
  );
}

// --------------------------------------------------------------------------- //
// Toasts — one polite live region
// --------------------------------------------------------------------------- //

interface Toast {
  readonly id: number;
  readonly message: string;
}

interface ToastValue {
  readonly announce: (message: string) => void;
}

const ToastContext = createContext<ToastValue | null>(null);

export function ToastProvider({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  const [toasts, setToasts] = useState<readonly Toast[]>([]);
  const counter = useRef(0);
  const expiries = useRef(new Set<number>());

  const announce = useCallback((message: string) => {
    counter.current += 1;
    const id = counter.current;
    setToasts((current) => [...current, { id, message }]);
    // Five seconds, per the handoff. The toast never carries the only copy of
    // a result — the page state already shows it — so expiring is safe.
    const expiry = window.setTimeout(() => {
      expiries.current.delete(expiry);
      setToasts((current) => current.filter((toast) => toast.id !== id));
    }, 5000);
    expiries.current.add(expiry);
  }, []);

  // A pending expiry must not outlive the provider. Unattended, it fires five
  // seconds later and calls setToasts into a tree that is gone — which under
  // test means after the DOM environment has been disposed, so `window` no
  // longer exists and the whole run fails on an unhandled ReferenceError even
  // though every test passed. It is a real leak either way; the test run is
  // just where it becomes visible.
  useEffect(() => {
    const pending = expiries.current;
    return () => {
      for (const expiry of pending) window.clearTimeout(expiry);
      pending.clear();
    };
  }, []);

  const value = useMemo<ToastValue>(() => ({ announce }), [announce]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div aria-live="polite" className="mj-toast-region">
        {toasts.map((toast) => (
          <div key={toast.id} className="mj-toast">
            {toast.message}
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToasts(): ToastValue {
  const value = useContext(ToastContext);
  if (value === null) throw new Error('useToasts must be used inside a ToastProvider.');
  return value;
}

/** Screen-reader-only text, for names that need no pixels. */
export function VisuallyHidden({
  children,
}: {
  readonly children: ReactNode;
}): ReactNode {
  return <span className="mj-visually-hidden">{children}</span>;
}
